import { Navigate, RouteObject } from 'react-router-dom';
import { AuthenticationCallbackWrapper } from '../Views/AuthenticationCallback';
import { DashboardPage } from '@features/dashboard';
import { RootErrorBoundary } from '../components/errors/RootErrorBoundary';
import { EditNoteTemplate } from '@features/notes';
import NoteJotDocView from '../features/notes/routes/NoteJotDocView';
import UserView from '../Views/UserView';
import UserNotesView from '../Views/UserNotesView';
import { NoteProcessingView } from '../Views/NoteProcessingView';
import { AdminViewOnly } from '../Views/Admin/AdminViewOnly';
import { TranscribeView } from '../Views/Admin/TranscribeView';
import ViewTemplate from '../Views/Shared/ViewTemplate';
import ViewUserTemplates from '../Views/Shared/ViewUserTemplates';
import {
  CliniciansPage,
  DataSecurityPage,
  OrganizationManagementPage,
  PracticeSettingsPage,
} from '@features/organizations';
import { AddOnBundlesPage, SubscriptionsPage } from '@features/subscriptions';
import { BillingSettingsPage } from '@features/billing-readiness';
import { RecorderView } from '../Views/RecorderView';
import { TestNoteRecordingView } from '../Views/TestNoteRecordingView';
import { PatientsView } from '../Views/PatientsView';
import PatientDetailView from '../components/Patient/PatientDetailView';
import { ClaimSubmittedView } from '../Views/ClaimSubmittedView';
import CheckoutSuccess from '../Views/CheckoutSuccess';
import CheckoutCancel from '../Views/CheckoutCancel';
import { EncounterPage, FirstEncounterPage } from '@features/encounters';
import { ScheduleView } from '@features/appointments';
import { DocumentsPage, LabsPage, FormsPage, DocumentTemplatesPage } from '@features/documents';
import FormDetailFullView from '../Views/FormDetailFullView';
import TemplateBuilderView from '../components/TemplateBuilder/TemplateBuilderView';
import { TemplatesPage } from '@features/templates';
import { OnboardingView } from '@features/onboarding';
import { AuthenticationSignupWrapper } from '../Views/Admin/UserSignup';
import { AuthenticatedLayout } from './layouts/AuthenticatedLayout';
import { ActionsBarLayout } from './layouts/ActionsBarLayout';
import { PracticeLayout } from './layouts/PracticeLayout';
import { Auth0ProviderWithNavigate } from '../auth0-provider';
import GoogleAuthCallback from '../Views/GoogleAuthCallback';
import { GoogleAuthCallbackWrapper } from '../Views/GoogleAuthCallbackWrapper';
import { DiagnosticsView } from '@views/DiagnosticsView';
import { RecordingPermissionGate } from '../components/guards/RecordingPermissionGate';
import { BillingLandingView } from '../Views/BillingLandingView';
import { ClaimDetailView } from '../Views/ClaimDetailView';
import { ClaimView } from '../Views/ClaimView';
import { BillInsurancePage } from '@features/billing';
import { ReportsAnalyticsPage } from '@features/analytics';
import { CoachPage } from '@features/jotcoach';
import {
  JotAuditPageWrapper,
  ProviderDetail,
  NoteAuditDetail,
  RuleAnalyticsPage,
  EncounterAuditsList,
  EncounterAuditDetail,
} from '@features/jotaudit';
import { PatientCommunicationsPage } from '@features/patients';
import { BillPatientsPage } from '@features/payments';
import { EligibilityChecksPage } from '@features/eligibility-checks';
import { PayerSearchPage } from '@features/payer-search';
import { MergeCustomizationPage, PatientDeduplicationPage } from '@features/patient-deduplication';

export const mainRoutes: RouteObject[] = [
  { path: '/callback', element: <AuthenticationCallbackWrapper /> },
  {
    path: '/google-auth-callback',
    element: <GoogleAuthCallbackWrapper />,
  },
  {
    path: '/signup',
    element: <AuthenticationSignupWrapper />,
  },
  {
    path: '/share/templates/:templateId',
    element: <ViewTemplate />,
  },
  {
    path: '/share/templates/created-by/:userId',
    element: <ViewUserTemplates />,
  },
  {
    path: '/',
    element: <AuthenticatedLayout />,
    errorElement: (
      <Auth0ProviderWithNavigate>
        <RootErrorBoundary />
      </Auth0ProviderWithNavigate>
    ),
    children: [
      {
        path: '/',
        index: true,
        element: <DashboardPage />,
      },
      {
        path: '/dashboard',
        element: <DashboardPage />,
      },
      {
        path: '/templatebuilder',
        element: <TemplateBuilderView />,
      },
      // {
      //   path: '/billing',
      //   element: <ClaimSubmittedView />,
      // },
      {
        path: '/billing',
        element: <BillingLandingView />,
      },
      {
        path: '/bill-insurance',
        element: <BillInsurancePage />,
      },
      {
        path: '/reports-analytics',
        element: <ReportsAnalyticsPage />,
      },
      {
        path: '/bill-patients',
        element: <BillPatientsPage />,
      },
      {
        path: '/communications',
        element: <PatientCommunicationsPage />,
      },
      {
        path: '/eligibility-checks',
        element: <EligibilityChecksPage />,
      },
      {
        path: '/payer-search',
        element: <PayerSearchPage />,
      },
      {
        path: '/jotcoach',
        element: <CoachPage />,
      },
      {
        path: '/jotaudit/rules/:ruleId/analytics',
        element: <RuleAnalyticsPage />,
      },
      {
        path: '/jotaudit/provider/:userId',
        element: <ProviderDetail />,
      },
      {
        path: '/jotaudit/note/:noteId',
        element: <NoteAuditDetail />,
      },
      {
        path: '/jotaudit/encounters',
        element: <EncounterAuditsList />,
      },
      {
        path: '/jotaudit/encounter/:encounterId',
        element: <EncounterAuditDetail />,
      },
      {
        path: '/jotaudit',
        element: <JotAuditPageWrapper />,
      },
      // Rule Build routes
      {
        path: '/rule-build',
        async lazy() {
          const { RuleBuildPage } = await import('@features/rule-build');
          return { Component: RuleBuildPage };
        },
      },
      {
        path: '/rule-build/:taskId',
        async lazy() {
          const { RuleBuildPage } = await import('@features/rule-build');
          return { Component: RuleBuildPage };
        },
      },
      {
        path: '/billing/claim/:claimId',
        element: <ClaimView />,
      },
      {
        path: '/billing/claim/:claimId/edit',
        element: <ClaimDetailView />,
      },
      {
        path: '/patients/',
        element: <PatientsView />,
      },
      {
        path: '/patients/merge',
        element: <PatientDeduplicationPage />,
      },
      {
        path: '/patients/merge/customize',
        element: <MergeCustomizationPage />,
      },
      {
        path: '/patient/:patientId',
        element: <PatientDetailView />,
      },
      {
        path: '/notes/',
        element: <UserNotesView />,
      },
      {
        path: '/templates/',
        element: <TemplatesPage />,
      },
      {
        path: '/templates/build',
        async lazy() {
          const { TemplateBuildPage } = await import('@features/templates-ai-edit');
          return { Component: TemplateBuildPage };
        },
      },
      {
        path: '/templates/build/:taskId',
        async lazy() {
          const { TemplateBuildPage } = await import('@features/templates-ai-edit');
          return { Component: TemplateBuildPage };
        },
      },
      {
        path: '/onboarding/',
        element: <OnboardingView />,
      },
      {
        path: '/record/',
        element: (
          <RecordingPermissionGate>
            <RecorderView />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/schedule',
        element: <ScheduleView />,
      },
      {
        path: '/documents',
        element: <DocumentsPage />,
      },
      {
        path: '/document-templates',
        element: <DocumentTemplatesPage />,
      },
      {
        path: '/labs',
        element: <LabsPage />,
      },
      {
        path: '/form-documents',
        element: <FormsPage />,
      },
      {
        path: '/forms',
        async lazy() {
          const { LibraryView } = await import('@features/form-library');
          return { Component: LibraryView };
        },
      },
      {
        path: '/rules',
        async lazy() {
          const { RulesManagementPage } = await import('@features/rules');
          return { Component: RulesManagementPage };
        },
      },
      {
        path: '/form-builder/:formId',
        async lazy() {
          const { BuilderPage } = await import('@features/form-builder');
          return { Component: BuilderPage };
        },
      },
      {
        path: '/encounters/:encounterId',
        element: (
          <RecordingPermissionGate>
            <EncounterPage />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/first-encounter',
        element: (
          <RecordingPermissionGate>
            <FirstEncounterPage />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/first-encounter/:encounterId',
        element: (
          <RecordingPermissionGate>
            <FirstEncounterPage />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/forms/:formSessionId',
        element: <FormDetailFullView />,
      },
      {
        path: '/exampleRecord/',
        element: <TestNoteRecordingView />,
      },
      {
        path: '/note/:noteId',
        children: [
          {
            index: true,
            async lazy() {
              const { NoteChatView } = await import('@features/note-ai-edit');
              return { Component: NoteChatView };
            },
          },
          {
            path: 'edit-template',
            element: <EditNoteTemplate />,
          },
        ],
      },
      {
        path: '/jotdoc/:jotdocId',
        element: <ActionsBarLayout />,
        children: [
          {
            index: true,
            element: <NoteJotDocView />,
          },
        ],
      },
      {
        path: '/status/:noteId',
        element: <NoteProcessingView />,
      },
      {
        path: '/transcribe',
        element: (
          <AdminViewOnly>
            <TranscribeView />
          </AdminViewOnly>
        ),
      },
      {
        path: '/user',
        element: <UserView />,
      },
      {
        path: '/user/access',
        element: <Navigate to="/user?tab=my-access" replace />,
      },
      {
        path: '/group_administration',
        element: <Navigate to="/practice/clinicians" replace />,
      },
      {
        path: '/practice',
        element: <PracticeLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/practice/clinicians" replace />,
          },
          {
            path: 'clinicians',
            element: <CliniciansPage />,
          },
          {
            path: 'settings',
            element: <PracticeSettingsPage />,
          },
          {
            path: 'subscriptions',
            element: <SubscriptionsPage />,
          },
          {
            path: 'data-security',
            element: <DataSecurityPage />,
          },
          {
            path: 'billing-settings',
            element: <BillingSettingsPage />,
          },
        ],
      },
      {
        path: '/bundles',
        element: <AddOnBundlesPage />,
      },
      {
        path: '/checkout-success',
        element: <CheckoutSuccess />,
      },
      {
        path: '/checkout-cancel',
        element: <CheckoutCancel />,
      },
      {
        path: '/google-auth-callback',
        element: <GoogleAuthCallback />,
      },
      {
        path: '/diagnostics',
        element: <DiagnosticsView />,
      },
    ],
  },
];
